import type { MilestoneDesign } from './milestoneDefaults';
import { DEMO_MILESTONE_TIERS } from './milestoneDefaults';
import { t } from '../../i18n';

interface Tier {
  id: string;
  threshold: number;
  label: string;
  rewardType?: string;
  unlocked?: boolean;
  isNext?: boolean;
}

interface MilestonePreviewProps {
  design: MilestoneDesign;
  mode: 'desktop' | 'mobile';
  /** 0–100 track fill for preview */
  progressPercent?: number;
  tiers?: Tier[];
  /** Show the details panel open */
  detailsOpen?: boolean;
  chipText?: string;
  title?: string;
}

function nodeIcon(t: Tier, unlocked: boolean) {
  if (unlocked) return '✓';
  const rt = String(t.rewardType || '').toLowerCase();
  if (rt.includes('ship')) return '🚚';
  if (rt.includes('gift')) return '🎁';
  if (rt.includes('order') || rt.includes('discount')) return '🏷️';
  return '★';
}

/** Live preview of the milestone product-block widget */
export function MilestonePreview({
  design,
  mode,
  progressPercent = 28,
  tiers = DEMO_MILESTONE_TIERS,
  detailsOpen = false,
  chipText,
  title,
}: MilestonePreviewProps) {
  const isMobile = mode === 'mobile';
  const list = tiers.slice(0, 4);
  const pct = Math.min(100, Math.max(0, progressPercent));
  const titleText = title || design.fallbackTitle;
  const chip =
    chipText ||
    `Spend $${list[list.length - 1]?.threshold || 150} · unlock all rewards`;

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: isMobile ? 20 : 16,
        overflow: 'hidden',
        background: isMobile
          ? 'linear-gradient(180deg, #e8ecef 0%, #d5dbe0 100%)'
          : 'linear-gradient(180deg, #eef0f2, #e2e5e8)',
        minHeight: isMobile ? 420 : 360,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 16 : 28,
        fontFamily: design.fontFamily,
      }}
    >
      {/* Fake product page chrome */}
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            inset: 16,
            borderRadius: 12,
            background:
              'linear-gradient(180deg, #f7f8f9 0%, #eceef0 40%, #e4e7ea 100%)',
            opacity: 0.95,
            zIndex: 0,
          }}
        />
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: isMobile ? '100%' : Math.min(design.maxWidth, 420),
          background: design.backgroundColor,
          color: design.textColor,
          borderRadius: design.borderRadius,
          boxShadow: '0 12px 32px rgba(0,0,0,.12)',
          padding: 0,
          fontFamily: design.fontFamily,
        }}
      >
        <div
          style={{
            margin: '0 0 16px',
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.35,
          }}
        >
          {titleText}
        </div>

        {/* Track + nodes */}
        <div style={{ position: 'relative', padding: '0 6px 4px', marginBottom: 12 }}>
          <div
            style={{
              position: 'absolute',
              left: 22,
              right: 22,
              top: 14,
              height: 4,
              // Rail tint uses Progress line so the color is visible at any %
              background: `color-mix(in srgb, ${design.lineColor} 20%, rgba(0,0,0,.08))`,
              borderRadius: 99,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.max(pct, 8)}%`,
                minWidth: pct > 0 ? undefined : 8,
                background: design.lineColor,
                borderRadius: 99,
                transition: 'width .35s ease',
                boxShadow: `0 0 8px ${design.lineColor}40`,
              }}
            />
          </div>
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            {list.map((t) => {
              const unlocked = !!t.unlocked;
              const isNext = !!t.isNext && !unlocked;
              return (
                <div
                  key={t.id}
                  style={{
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 0,
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      background: unlocked ? design.accentColor : '#fff',
                      border: `2.5px solid ${
                        unlocked || isNext
                          ? design.accentColor
                          : 'rgba(0,0,0,.2)'
                      }`,
                      color: unlocked
                        ? '#fff'
                        : isNext
                          ? design.accentColor
                          : 'rgba(0,0,0,.35)',
                      boxShadow: isNext
                        ? `0 0 0 4px ${design.accentColor}2e`
                        : undefined,
                      transform: unlocked ? 'scale(1.05)' : undefined,
                    }}
                  >
                    {nodeIcon(t, unlocked)}
                  </div>
                  <div
                    style={{
                      marginTop: 7,
                      fontSize: 11,
                      fontWeight: unlocked || isNext ? 600 : 500,
                      color:
                        unlocked || isNext ? design.textColor : design.mutedColor,
                      lineHeight: 1.25,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      padding: '0 2px',
                    }}
                  >
                    {t.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {design.showChip && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 4,
              padding: '9px 12px',
              background: 'rgba(255,255,255,.55)',
              border: '1px solid rgba(0,0,0,.08)',
              borderRadius: 999,
              fontSize: 12,
              lineHeight: 1.3,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: '#fff',
                border: '1px solid rgba(0,0,0,.06)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              🎁
            </span>
            <span style={{ flex: 1, minWidth: 0, color: design.textColor }}>
              {chip}
            </span>
            {design.showDetails && (
              <span
                style={{
                  flexShrink: 0,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  color: design.textColor,
                  whiteSpace: 'nowrap',
                }}
              >
                {design.detailsLabel}
              </span>
            )}
          </div>
        )}

        {detailsOpen && design.showDetails && (
          <div
            style={{
              marginTop: 12,
              padding: '12px 12px 8px',
              background: design.detailsBackgroundColor,
              border: `1px solid ${design.detailsBorderColor}`,
              borderRadius: 12,
              color: design.detailsTextColor,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 8,
                color: design.detailsTextColor,
              }}
            >
              {design.detailsTitle}
            </div>
            {list.map((tier, idx) => {
              const unlocked = !!tier.unlocked;
              const isNext = !!tier.isNext && !unlocked;
              const dAccent = design.detailsAccentColor || design.accentColor;
              return (
                <div
                  key={tier.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 8px',
                    marginBottom: 6,
                    borderRadius: 8,
                    border: `1px solid ${
                      unlocked || isNext
                        ? `${dAccent}59`
                        : design.detailsBorderColor
                    }`,
                    background: unlocked
                      ? `${dAccent}14`
                      : design.detailsItemBackgroundColor,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      background: unlocked
                        ? dAccent
                        : design.detailsItemBackgroundColor,
                      border: `2px solid ${
                        unlocked || isNext ? dAccent : design.detailsBorderColor
                      }`,
                      color: unlocked
                        ? '#fff'
                        : isNext
                          ? dAccent
                          : design.detailsMutedColor,
                    }}
                  >
                    {unlocked ? '✓' : idx + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: design.detailsTextColor,
                      }}
                    >
                      {tier.label}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: design.detailsMutedColor,
                      }}
                    >
                      {t('Spend ${{amount}}', { amount: tier.threshold })}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color:
                        unlocked || isNext
                          ? dAccent
                          : design.detailsMutedColor,
                    }}
                  >
                    {unlocked ? 'Unlocked' : isNext ? 'Next' : 'Locked'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
